import React, { useState, useEffect } from "react";
import { Check, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import { API_URLS } from "../api/urls";
import GSTCalculator, { calculateGST } from "../ui/GSTCalculator";
import RazorpayService, {
  RazorpayResponse,
} from "../../services/razorpayService";
import { useToast } from "../ui/Toastcontext";

const getPlanColors = (planId: string) => {
  switch (planId) {
    case "lite":
      return {
        bg: "bg-[#FDF7FC]",
        border: "border-[#E47DCB]",
        accent: "text-[#E47DCB]",
      };
    case "spark":
      return {
        bg: "bg-[#FCF8F0]",
        border: "border-[#C5A344]",
        accent: "text-[#C5A344]",
      };
    case "ignite":
      return {
        bg: "bg-[#F5FEE1]",
        border: "border-[#86BC0B]",
        accent: "text-[#86BC0B]",
      };
    case "thrive":
      return {
        bg: "bg-[#F1EDFD]",
        border: "border-[#B7AAF5]",
        accent: "text-[#B7AAF5]",
      };
    case "signature":
      return {
        bg: "bg-[#F9FEFE]",
        border: "border-[#6DD0CF]",
        accent: "text-[#6DD0CF]",
      };
    default:
      return {
        bg: "bg-white",
        border: "border-gray-200",
        accent: "text-gray-500",
      };
  }
};

// API Response Types
interface PaymentApiResponse {
  success: string;
  message: string;
  order_id: string;
  user_payment_id: number;
  invoice_number?: string;
}

interface UpdatePaymentStatusRequest {
  user_id: number;
  user_payment_id: number;
  order_id: string;
  payment_id: string;
  signature: string;
  payment_status: string;
}

interface UpdatePaymentStatusResponse {
  success: string;
  message: string;
  payment_id?: string;
  signature_id?: string;
  invoice_number?: string;
  [key: string]: any;
}

// Plan API Types
interface PlanDetail {
  subscription_plan_id: number;
  subscription_plan_name: string;
  std_subscription_perks: string;
  duration_and_price_and_perks: {
    standard_details_id: number;
    std_subscription_price: string;
    std_subscription_discount_price: string;
    duration_period: string;
    product_id: string;
    std_subscription_description: string;
    std_subscription_perfect_for: string;
    std_subscription_not_included: string;
  }[];
}

interface PlansApiResponse {
  success: string;
  message: string;
  plans: PlanDetail[];
}

interface UserInfo {
  user_fname: string;
  user_lname: string;
  user_mobileno: string;
  email: string;
  profile: string;
}

interface GetUserInfoResponse {
  success: string;
  message: string;
  user_info: UserInfo;
}

interface UpdateUserInfoResponse {
  success: string;
  message: string;
}

interface RecommendedPlanDetailsForDesktopProps {
  planType: "lite" | "spark" | "ignite" | "thrive" | "signature";
  selectedDuration?: string;
  userId?: number | null;
  onBack?: () => void;
  userData?: {
    user_fname?: string;
    user_lname?: string;
  };
  onPaymentSuccess?: (
    planType: string,
    duration: number,
    paymentDetails: {
      orderId: string;
      paymentId: string;
      amount: number;
      transactionDate: string;
      apiPaymentId?: string;
      signatureId?: string;
      invoiceNumber?: string;
      userPaymentId?: number;
    }
  ) => void;
  selectedPlanData?: {
    id: string;
    title: string;
    subtitle: string;
    description: string;
    pricingOptions: {
      duration: string;
      price: string;
      originalPrice?: string;
      standardDetailsId: number;
      productId: string;
    }[];
    whatYouGet: string[];
    whatsNotIncluded: string[];
    featuresTable?: {
      headers: string[];
      rows: {
        feature: string;
        values: string[];
      }[];
    };
  };
  preSelectedPrice?: number;
  isComplimentary?: boolean;
  preFilledEmail?: string;
}

const RecommendedPlanDetailsForDesktop: React.FC<
  RecommendedPlanDetailsForDesktopProps
> = ({
  planType,
  selectedDuration,
  userId,
  onBack,
  userData,
  onPaymentSuccess,
  selectedPlanData,
  preSelectedPrice,
  isComplimentary = false,
  preFilledEmail,
}) => {
  const { showToast } = useToast();
  const [showPlanDetails, setShowPlanDetails] = useState(false);
  const [selectedPricingOption, setSelectedPricingOption] = useState<
    string | null
  >(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<
    "idle" | "processing" | "success" | "failed"
  >("idle");
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const [currentUserPaymentId, setCurrentUserPaymentId] = useState<
    number | null
  >(null);

  // State for API data (fallback when selectedPlanData is not provided)
  const [planDetails, setPlanDetails] = useState<PlanDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [features, setFeatures] = useState<string[]>([]);
  const [notIncluded, setNotIncluded] = useState<string[]>([]);

  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [isEmailExisting, setIsEmailExisting] = useState<boolean>(false);
  const [isLoadingEmail, setIsLoadingEmail] = useState<boolean>(true);

  // Stores the exact error message from the API to show in the inline banner
  const [paymentErrorMessage, setPaymentErrorMessage] = useState<string | null>(null);

  const razorpayService = RazorpayService.getInstance();

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Fetch user email on component mount
  useEffect(() => {
    const fetchUserEmail = async () => {
      if (!userId) {
        setIsLoadingEmail(false);
        return;
      }

      try {
        const getUserResponse = await fetch(
          `${API_URLS.GET_USER_INFO}${userId}`
        );

        if (!getUserResponse.ok) {
          throw new Error("Failed to fetch user information");
        }

        const userDataResponse: GetUserInfoResponse =
          await getUserResponse.json();

        if (userDataResponse.success !== "1") {
          throw new Error("Failed to fetch user information");
        }

        const fetchedEmail = (userDataResponse.user_info.email || "").trim();
        console.log("Email fetch for userId", userId, "->", fetchedEmail || "(empty)");

        if (fetchedEmail !== "") {
          setEmail(fetchedEmail);
          setIsEmailExisting(true);
        } else if (preFilledEmail) {
          setEmail(preFilledEmail);
          setIsEmailExisting(false);
        } else {
          setEmail("");
          setIsEmailExisting(false);
        }
      } catch (error) {
        // Don't block payment — allow manual email entry if fetch fails
        console.error("Error fetching user email:", error);
        setEmail("");
        setIsEmailExisting(false);
      } finally {
        setIsLoadingEmail(false);
      }
    };

    fetchUserEmail();
  }, [userId, preFilledEmail]);

  // Handle email change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) {
      setEmailError("");
    }
  };

  // Validate email before payment
  const validateEmailBeforePayment = (): boolean => {
    if (!email.trim()) {
      setEmailError("Email is required");
      return false;
    }
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const updateUserEmail = async (userEmail: string): Promise<boolean> => {
    if (!userId) {
      console.error("User ID not found");
      return false;
    }

    try {
      const getUserResponse = await fetch(`${API_URLS.GET_USER_INFO}${userId}`);

      if (!getUserResponse.ok) {
        throw new Error("Failed to fetch current user information");
      }

      const userDataResponse: GetUserInfoResponse =
        await getUserResponse.json();

      if (userDataResponse.success !== "1") {
        throw new Error("Failed to fetch current user information");
      }

      const formData = new FormData();
      formData.append("user_id", userId.toString());
      formData.append("user_fname", userDataResponse.user_info.user_fname || "");
      formData.append("user_lname", userDataResponse.user_info.user_lname || "");
      formData.append(
        "user_mobileno",
        userDataResponse.user_info.user_mobileno || ""
      );
      formData.append("email", userEmail.trim());

      if (
        userDataResponse.user_info.profile &&
        userDataResponse.user_info.profile !== ""
      ) {
        formData.append("profile", userDataResponse.user_info.profile);
      }

      const updateResponse = await fetch(API_URLS.UPDATE_USER_INFO, {
        method: "POST",
        body: formData,
      });

      if (!updateResponse.ok) {
        throw new Error(`HTTP error! status: ${updateResponse.status}`);
      }

      const updateData: UpdateUserInfoResponse = await updateResponse.json();

      if (updateData.success !== "1") {
        throw new Error(updateData.message || "Failed to update email");
      }

      return true;
    } catch (error) {
      console.error("Error updating user email:", error);
      setEmailError(
        error instanceof Error ? error.message : "Failed to update email"
      );
      return false;
    }
  };

  // Determine if we should use provided data or fetch from API
  const shouldFetchFromAPI = !selectedPlanData;

  // Initialize data from props or fetch from API
  useEffect(() => {
    if (selectedPlanData) {
      setFeatures(selectedPlanData.whatYouGet);
      setNotIncluded(selectedPlanData.whatsNotIncluded);

      if (selectedDuration && selectedPlanData.pricingOptions) {
        const matchingOption = selectedPlanData.pricingOptions.find(
          (option) =>
            option.duration === selectedDuration ||
            option.duration.toLowerCase() === selectedDuration.toLowerCase()
        );
        if (matchingOption) {
          setSelectedPricingOption(matchingOption.duration);
        } else if (selectedPlanData.pricingOptions[0]) {
          setSelectedPricingOption(selectedPlanData.pricingOptions[0].duration);
        }
      } else if (selectedPlanData.pricingOptions?.[0]) {
        setSelectedPricingOption(selectedPlanData.pricingOptions[0].duration);
      }

      setLoading(false);
    } else {
      fetchPlanDetails();
    }
  }, [selectedPlanData, selectedDuration, planType]);

  // Fetch plan details from API (fallback method)
  const fetchPlanDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(API_URLS.Get_STANDARD_PLANS, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: PlansApiResponse = await response.json();

      if (data.success !== "1") {
        throw new Error(data.message || "Failed to fetch plans");
      }

      const planMapping: Record<string, string> = {
        lite: "Triva Lite",
        spark: "Triva Spark",
        ignite: "Triva Ignite",
        thrive: "Triva Thrive",
        signature: "Triva Signature",
      };

      const matchedPlan = data.plans?.find((plan: PlanDetail) =>
        plan.subscription_plan_name
          .toLowerCase()
          .includes(planMapping[planType].toLowerCase())
      );

      if (matchedPlan) {
        setPlanDetails(matchedPlan);

        const parsedFeatures = parsePerksToArray(
          matchedPlan.std_subscription_perks
        );
        setFeatures(parsedFeatures);

        if (
          matchedPlan.duration_and_price_and_perks?.[0]
            ?.std_subscription_not_included
        ) {
          const parsedNotIncluded = parsePerksToArray(
            matchedPlan.duration_and_price_and_perks[0]
              .std_subscription_not_included
          );
          setNotIncluded(parsedNotIncluded);
        }

        if (selectedDuration) {
          const matchingOption = matchedPlan.duration_and_price_and_perks.find(
            (option) => option.duration_period === selectedDuration
          );

          if (matchingOption) {
            setSelectedPricingOption(matchingOption.duration_period);
          } else if (matchedPlan.duration_and_price_and_perks?.[0]) {
            setSelectedPricingOption(
              matchedPlan.duration_and_price_and_perks[0].duration_period
            );
          }
        } else if (matchedPlan.duration_and_price_and_perks?.[0]) {
          setSelectedPricingOption(
            matchedPlan.duration_and_price_and_perks[0].duration_period
          );
        }
      } else {
        setError("Plan details not found");
      }
    } catch (err) {
      console.error("Error fetching plan details:", err);
      setError("Failed to load plan details");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to parse perks string into array
  const parsePerksToArray = (perks: string): string[] => {
    if (!perks) return [];

    let cleanedPerks = perks
      .replace(/<[^>]*>/g, "")
      .replace(/\\r\\n|\\r|\\n|\r\n|\r|\n/g, "|BREAK|")
      .trim();

    if (!cleanedPerks.includes("|BREAK|")) {
      return cleanedPerks ? [cleanedPerks] : [];
    }

    return cleanedPerks
      .split("|BREAK|")
      .map((perk) => perk.trim())
      .filter((perk) => perk && perk.length > 0)
      .map((perk) => perk.replace(/,$/, ""));
  };

  // Get plan display name
  const getPlanDisplayName = () => {
    if (selectedPlanData) {
      return selectedPlanData.title;
    }

    if (planDetails?.subscription_plan_name) {
      const planNameMatch =
        planDetails.subscription_plan_name.match(/^([^(]+)/);
      return planNameMatch
        ? planNameMatch[1].trim()
        : planDetails.subscription_plan_name;
    }

    const planNames: Record<string, string> = {
      lite: "Triva Lite",
      spark: "Triva Spark",
      ignite: "Triva Ignite",
      thrive: "Triva Thrive",
      signature: "Triva Signature",
    };

    return planNames[planType] || "Triva Plan";
  };

  const userName = userData?.user_fname
    ? `${userData.user_fname} ${userData.user_lname || ""}`.trim()
    : "User";

  // Get selected plan price with priority to provided data
  const getSelectedPlanPrice = (): number => {
    // Priority 1: Use preSelectedPrice if provided and valid
    if (preSelectedPrice && preSelectedPrice > 0) {
      return preSelectedPrice;
    }

    // Priority 2: Use selectedPlanData if available
    if (selectedPlanData?.pricingOptions) {
      const targetDuration = selectedPricingOption || selectedDuration;
      if (targetDuration) {
        const option = selectedPlanData.pricingOptions.find(
          (opt) =>
            opt.duration === targetDuration ||
            opt.duration.toLowerCase() === targetDuration.toLowerCase()
        );
        if (option) {
          const parsed = parseFloat(option.price);
          if (!isNaN(parsed) && parsed > 0) return parsed;
        }
      }
      if (selectedPlanData.pricingOptions[0]) {
        const parsed = parseFloat(selectedPlanData.pricingOptions[0].price);
        if (!isNaN(parsed) && parsed > 0) return parsed;
      }
    }

    // Priority 3: Fall back to API data
    if (planDetails?.duration_and_price_and_perks) {
      const targetDuration = selectedPricingOption || selectedDuration;
      if (targetDuration) {
        const option = planDetails.duration_and_price_and_perks.find(
          (opt) => opt.duration_period === targetDuration
        );
        if (option) {
          const price =
            option.std_subscription_discount_price ||
            option.std_subscription_price;
          const parsed = parseFloat(price);
          if (!isNaN(parsed) && parsed > 0) return parsed;
        }
      }
      if (planDetails.duration_and_price_and_perks[0]) {
        const first = planDetails.duration_and_price_and_perks[0];
        const price =
          first.std_subscription_discount_price || first.std_subscription_price;
        const parsed = parseFloat(price);
        if (!isNaN(parsed) && parsed > 0) return parsed;
      }
    }

    return 0;
  };

  const selectedPlanPrice = getSelectedPlanPrice();
  const gstCalculation = calculateGST(selectedPlanPrice);

  // Helper function to extract duration in months
  const extractDurationInMonths = (planString: string): number => {
    const daysMatch = planString.match(/(\d+)\s*day/i);
    if (daysMatch) {
      return parseInt(daysMatch[1], 10) / 30;
    }
    if (planString.includes("1 month") || planString.includes("1 Month"))
      return 1;
    if (planString.includes("3 month") || planString.includes("3 Months"))
      return 3;
    if (planString.includes("6 month") || planString.includes("6 Months"))
      return 6;
    if (planString.includes("1 time")) return 1;
    return 1;
  };

  // ── Robustly resolve standardDetailsId from either data source ──
  // Falls back through: selectedPricingOption → selectedDuration → first option
  const getStandardDetailsId = (): number => {
    if (selectedPlanData?.pricingOptions) {
      const targetDuration = selectedPricingOption || selectedDuration;
      if (targetDuration) {
        const opt = selectedPlanData.pricingOptions.find(
          (o) =>
            o.duration === targetDuration ||
            o.duration.toLowerCase() === targetDuration.toLowerCase()
        );
        if (opt?.standardDetailsId) return opt.standardDetailsId;
      }
      // Last resort: first option
      if (selectedPlanData.pricingOptions[0]?.standardDetailsId) {
        return selectedPlanData.pricingOptions[0].standardDetailsId;
      }
    }

    if (planDetails?.duration_and_price_and_perks) {
      const targetDuration = selectedPricingOption || selectedDuration;
      if (targetDuration) {
        const opt = planDetails.duration_and_price_and_perks.find(
          (o) => o.duration_period === targetDuration
        );
        if (opt?.standard_details_id) return opt.standard_details_id;
      }
      if (planDetails.duration_and_price_and_perks[0]?.standard_details_id) {
        return planDetails.duration_and_price_and_perks[0].standard_details_id;
      }
    }

    return 0;
  };

  // Update payment status
  const updatePaymentStatus = async (
    userId: number,
    userPaymentId: number,
    orderId: string,
    paymentId: string,
    signature: string,
    status: string = "success"
  ): Promise<UpdatePaymentStatusResponse> => {
    try {
      const updatePaymentData: UpdatePaymentStatusRequest = {
        user_id: userId,
        user_payment_id: userPaymentId,
        order_id: orderId,
        payment_id: paymentId,
        signature: signature,
        payment_status: status,
      };

      const response = await fetch(API_URLS.UPDATE_PAYMENT_STATUS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatePaymentData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status}, body: ${errorText}`
        );
      }

      const result: UpdatePaymentStatusResponse = await response.json();

      if (result.success !== "1") {
        throw new Error(result.message || "Failed to update payment status");
      }

      return result;
    } catch (error) {
      console.error("Payment status update failed:", error);
      throw error;
    }
  };

  const handlePaymentSuccess = async (
    response: RazorpayResponse,
    userPaymentId: number
  ) => {
    // Keep processing/loading state — navigate directly to success page without showing success banner
    setPaymentStatus("processing");
    setIsProcessingPayment(true);

    try {
      let apiResponse: UpdatePaymentStatusResponse | null = null;

      if (userId && response.razorpay_order_id) {
        apiResponse = await updatePaymentStatus(
          userId,
          userPaymentId,
          response.razorpay_order_id,
          response.razorpay_payment_id,
          response.razorpay_signature,
          "success"
        );
      }

      if (onPaymentSuccess && selectedPricingOption) {
        const durationInMonths = extractDurationInMonths(selectedPricingOption);
        const finalAmount = gstCalculation.total;

        const paymentDetails = {
          orderId: response.razorpay_order_id,
          paymentId: response.razorpay_payment_id,
          amount: finalAmount,
          transactionDate: new Date().toISOString(),
          apiPaymentId: apiResponse?.payment_id,
          signatureId: apiResponse?.signature_id,
          invoiceNumber: apiResponse?.invoice_number,
          userPaymentId: userPaymentId,
        };

        onPaymentSuccess(planType, durationInMonths, paymentDetails);
      }
    } catch (error) {
      console.error("Error in payment success handler:", error);
      setPaymentStatus("failed");
      setIsProcessingPayment(false);
      showToast(
        "warning",
        "Payment completed but status update failed. Please contact support if needed."
      );
    }
  };

  const handlePaymentFailure = async (error: any) => {
    setPaymentStatus("failed");
    setIsProcessingPayment(false);
    setPaymentErrorMessage("Payment failed. Please try again.");

    if (userId && currentUserPaymentId && currentOrderId) {
      try {
        await updatePaymentStatus(
          userId,
          currentUserPaymentId,
          currentOrderId,
          "",
          "",
          "failed"
        );
      } catch (updateError) {
        console.error("Failed to update payment status to failed:", updateError);
      }
    }

    setCurrentOrderId(null);
    setCurrentUserPaymentId(null);
    showToast("error", "Payment failed. Please try again.");
  };

  const doInitiatePayment = async () => {
    if (!userId) return;

    const basePrice = getSelectedPlanPrice();
    const standardDetailsId = getStandardDetailsId();

    if (basePrice <= 0 || !standardDetailsId) {
      setPaymentStatus("failed");
      setIsProcessingPayment(false);
      setPaymentErrorMessage("Payment failed. Please try again.");
      showToast("error", "Payment failed. Please try again.");
      return;
    }

    const gstCalc = calculateGST(basePrice);
    const paymentData = {
      fk_user_id: userId,
      payment_type: "Online",
      amount: parseFloat(gstCalc.total.toFixed(2)),
      payment_status: "pending",
      plan_type: "Standard",
      plan_Id: standardDetailsId,
    };

    setIsProcessingPayment(true);
    setPaymentStatus("processing");
    setPaymentErrorMessage(null);

    try {
      const response = await fetch(API_URLS.ADD_USER_PAYMENT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(paymentData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: PaymentApiResponse = await response.json();

      if (result.success !== "1") {
        const errorMsg = result.message || "Failed to create order";
        setPaymentStatus("failed");
        setIsProcessingPayment(false);
        setPaymentErrorMessage(errorMsg);
        showToast("error", `Payment initiation failed: ${errorMsg}`);
        return;
      }

      setCurrentOrderId(result.order_id);
      setCurrentUserPaymentId(result.user_payment_id);

      const effectiveDuration =
        selectedPricingOption ||
        selectedDuration ||
        selectedPlanData?.pricingOptions?.[0]?.duration ||
        "Plan";

      const paymentOptions = razorpayService.createPaymentOptions({
        orderId: result.order_id,
        amount: gstCalc.total,
        description: `${getPlanDisplayName()} - ${effectiveDuration}`,
        userDetails: {
          name: userData?.user_fname
            ? `${userData.user_fname} ${userData.user_lname || ""}`.trim()
            : "User",
          email: email,
          contact: "9876543210",
        },
        onSuccess: (razorpayResponse: RazorpayResponse) => {
          handlePaymentSuccess(razorpayResponse, result.user_payment_id);
        },
        onFailure: (err: any) => {
          handlePaymentFailure(err);
        },
        onDismiss: () => {
          handlePaymentDismiss();
        },
        notes: {
          plan_type: planType,
          duration: effectiveDuration,
          user_payment_id: result.user_payment_id,
          standard_details_id: standardDetailsId,
        },
      });

      await razorpayService.initiatePayment(paymentOptions);
    } catch (err) {
      console.error("Payment initiation failed:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Unknown error occurred";
      setPaymentStatus("failed");
      setIsProcessingPayment(false);
      setPaymentErrorMessage(errorMessage);
      showToast("error", `Payment initiation failed: ${errorMessage}`);
    }
  };

  const handlePaymentDismiss = async () => {
    setIsProcessingPayment(false);

    if (userId && currentUserPaymentId && currentOrderId) {
      // Modal was closed after a payment attempt — treat as failure
      setPaymentStatus("failed");
      setPaymentErrorMessage("Payment was not completed. Please try again.");
      try {
        await updatePaymentStatus(
          userId,
          currentUserPaymentId,
          currentOrderId,
          "",
          "",
          "cancelled"
        );
      } catch (updateError) {
        console.error(
          "Failed to update payment status to cancelled:",
          updateError
        );
      }
      setCurrentOrderId(null);
      setCurrentUserPaymentId(null);
      showToast("error", "Payment was not completed. Please try again.");
    } else {
      // No payment was attempted — user cancelled from the form
      setPaymentStatus("idle");
      setPaymentErrorMessage(null);
    }
  };

  const handlePayment = async () => {
    if (isComplimentary) {
      if (!userId) return;

      if (!isEmailExisting) {
        if (!validateEmailBeforePayment()) return;
        const emailUpdated = await updateUserEmail(email);
        if (!emailUpdated) {
          showToast("error", "Failed to update email. Please try again.");
          return;
        }
        setIsEmailExisting(true);
      }

      const standardDetailsId = getStandardDetailsId();
      if (!standardDetailsId) {
        showToast("error", "Plan details not found. Please try again.");
        return;
      }

      setIsProcessingPayment(true);
      setPaymentStatus("processing");
      setPaymentErrorMessage(null);

      try {
        const res = await fetch(API_URLS.ADD_USER_PAYMENT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fk_user_id: userId,
            payment_type: "Online",
            amount: 0,
            payment_status: "pending",
            plan_type: "Standard",
            plan_Id: standardDetailsId,
          }),
        });

        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

        const result: PaymentApiResponse = await res.json();

        if (result.success !== "1") {
          const msg = result.message || "Failed to activate plan";
          setPaymentStatus("failed");
          setIsProcessingPayment(false);
          setPaymentErrorMessage(msg);
          showToast("error", msg);
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 10000));

        if (onPaymentSuccess) {
          const effectiveDuration = selectedPricingOption || selectedDuration || "";
          const durationInMonths = extractDurationInMonths(effectiveDuration);
          onPaymentSuccess(planType, durationInMonths, {
            orderId: result.order_id || "",
            paymentId: "",
            amount: 0,
            transactionDate: new Date().toISOString(),
            invoiceNumber: result.invoice_number,
            userPaymentId: result.user_payment_id,
          });
        }
      } catch (error) {
        console.error("Complimentary plan activation failed:", error);
        setPaymentStatus("failed");
        setIsProcessingPayment(false);
        setPaymentErrorMessage(
          error instanceof Error ? error.message : "Activation failed"
        );
        showToast("error", "Failed to activate plan. Please try again.");
      }
      return;
    }
    console.log("=== handlePayment triggered ===");
    console.log("userId:", userId);
    console.log("selectedPricingOption:", selectedPricingOption);
    console.log("selectedDuration:", selectedDuration);
    console.log("isProcessingPayment:", isProcessingPayment);
    console.log("selectedPlanPrice:", selectedPlanPrice);
    console.log("email:", email);
    console.log("isEmailExisting:", isEmailExisting);
    console.log("isLoadingEmail:", isLoadingEmail);

    // Guard: userId
    if (!userId) {
      console.log("BLOCKED: no userId");
      showToast("error", "User session not found. Please log in again.");
      return;
    }
    console.log("PASSED: userId check");

    // Guard: already processing
    if (isProcessingPayment) {
      console.log("BLOCKED: already processing");
      return;
    }
    console.log("PASSED: isProcessingPayment check");

    // Guard: email only required when user has no saved email
    console.log("Email state -> value:", email, "isEmailExisting:", isEmailExisting);
    if (!isEmailExisting) {
      if (!validateEmailBeforePayment()) {
        console.log("BLOCKED: email validation failed, email value:", email);
        return;
      }
      console.log("Saving new email:", email);
      const emailUpdated = await updateUserEmail(email);
      console.log("Email update result:", emailUpdated);
      if (!emailUpdated) {
        showToast("error", "Failed to update email. Please try again.");
        return;
      }
      setIsEmailExisting(true);
    }
    console.log("PASSED: email check (existing or just saved)");

    // Guard: price
    const basePrice = getSelectedPlanPrice();
    console.log("basePrice:", basePrice);
    if (basePrice <= 0) {
      console.log("BLOCKED: basePrice <= 0");
      showToast("error", "Unable to determine plan price. Please try again.");
      return;
    }
    console.log("PASSED: price check");

    // Guard: standardDetailsId — uses robust fallback resolver
    const standardDetailsId = getStandardDetailsId();
    console.log("standardDetailsId:", standardDetailsId);
    if (!standardDetailsId) {
      console.log("BLOCKED: standardDetailsId is 0");
      showToast(
        "error",
        "Plan option details not found. Please go back and reselect your plan."
      );
      return;
    }
    console.log("PASSED: standardDetailsId check — proceeding to API call");

    await doInitiatePayment();
  };

  const getButtonText = () => {
    if (isComplimentary) {
      return paymentStatus === "processing" ? "Activating..." : "Activate Complimentary Plan";
    }
    switch (paymentStatus) {
      case "processing":
        return "Processing Payment...";
      case "success":
        return "Payment Successful!";
      case "failed":
        return "Retry Payment";
      default:
        return `Pay ₹ ${gstCalculation.total.toFixed(2)}`;
    }
  };

  const isButtonDisabled = (): boolean => {
    if (!userId) return true;
    if (paymentStatus === "processing") return true;
    if (paymentStatus === "success") return true;
    if (isLoadingEmail) return true;
    const isStillLoading = shouldFetchFromAPI && loading;
    if (!isComplimentary && !isStillLoading && selectedPlanPrice <= 0) return true;
    return false;
  };

  // Loading state
  if (shouldFetchFromAPI && loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0DB5B5] mx-auto mb-4"></div>
          <p className="text-[#646464]">Loading plan details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (shouldFetchFromAPI && (error || !planDetails)) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            Plan Not Found
          </h2>
          <p className="text-gray-500 mb-4">
            {error || "The selected plan details are not available."}
          </p>
          <button
            onClick={onBack}
            className="bg-[#0DB5B5] text-white px-6 py-2 rounded-lg hover:bg-[#0A9999] transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Processing transaction overlay */}
      {paymentStatus === "processing" && isProcessingPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="bg-white rounded-2xl px-16 py-14 flex flex-col items-center shadow-lg max-w-md w-full mx-4">
            <div className="w-2 h-2 rounded-full bg-gray-300 mb-8 animate-pulse" />
            <h2 className="text-2xl font-bold text-[#202020] mb-3 text-center">
              Processing your transaction
            </h2>
            <p className="text-[#646464] text-center text-sm leading-relaxed">
              Please do not refresh, go back,<br />or click anywhere.
            </p>
          </div>
        </div>
      )}

      <div className="w-full p-4 border border-gray-100 rounded-[18px]">
        <div className="max-w-2xl mx-auto px-6 py-8">
          {/* User Info Section */}
          <div className="grid grid-cols-2 gap-8 mb-6">
            <div>
              <label className="text-[14px] font-medium text-[#202020] mb-2 block">
                Full Name
              </label>
              <p className="text-[16px] text-[#8D8D8D]">{userName}</p>
            </div>
            <div>
              <label className="text-[14px] font-medium text-[#202020] mb-2 block">
                Subscription plan
              </label>
              <p className="text-[16px] text-[#8D8D8D]">
                {getPlanDisplayName()}{" "}
                {(selectedPricingOption || selectedDuration) &&
                  `(${(selectedPricingOption || selectedDuration)!
                    .replace(/For\s+/i, "")
                    .replace(/\s*@.*$/, "")})`}
              </p>
            </div>
          </div>

          {/* Email Input Field */}
          <div className="mb-6">
            <label className="text-[14px] font-medium text-[#202020] mb-2 block">
              Email{" "}
              {!isEmailExisting && <span className="text-red-500">*</span>}
            </label>
            {isLoadingEmail ? (
              <div className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-gray-50">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0DB5B5] mr-2"></div>
                  <span className="text-[14px] text-gray-500">
                    Loading email...
                  </span>
                </div>
              </div>
            ) : (
              <>
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder={isEmailExisting ? "" : "Enter your email"}
                  disabled={isEmailExisting}
                  className={`w-full px-4 py-3 rounded-lg text-[16px] transition-colors ${
                    isEmailExisting
                      ? "bg-gray-50 border border-gray-300 text-gray-700 cursor-not-allowed"
                      : emailError
                      ? "border-2 border-red-500 focus:outline-none focus:border-red-500"
                      : "border border-gray-300 focus:outline-none focus:border-[#0DB5B5]"
                  }`}
                />
                {isEmailExisting && (
                  <p className="mt-2 text-[12px] text-gray-500">
                    This email is registered with your account
                  </p>
                )}
                {emailError && !isEmailExisting && (
                  <p className="mt-2 text-[14px] text-red-500">{emailError}</p>
                )}
              </>
            )}
          </div>

          {/* Plan Details Dropdown */}
          <div className="border border-gray-200 rounded-lg mb-6">
            <button
              onClick={() => setShowPlanDetails(!showPlanDetails)}
              className="w-full px-4 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
            >
              <span className="text-[16px] font-medium text-[#202020]">
                Plan details
              </span>
              {showPlanDetails ? (
                <ChevronUp className="w-5 h-5 text-[#202020]" />
              ) : (
                <ChevronDown className="w-5 h-5 text-[#202020]" />
              )}
            </button>

            {showPlanDetails && (
              <div className="px-4 pb-4 border-t border-gray-100">
                {/* What you get */}
                <div className="mb-6 mt-4">
                  <h4 className="text-[16px] font-semibold text-[#202020] mb-3">
                    What you get:
                  </h4>
                  <div className="space-y-3">
                    {features.map((item, index) => (
                      <div key={index} className="flex items-start">
                        <Check className="w-4 h-4 text-[#0DB5B5] mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-[14px] text-[#202020] leading-relaxed">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* What's not included */}
                {notIncluded.length > 0 && (
                  <div>
                    <h4 className="text-[16px] font-semibold text-[#202020] mb-3">
                      What's not included:
                    </h4>
                    <div className="space-y-3">
                      {notIncluded.map((item, index) => (
                        <div key={index} className="flex items-start">
                          <AlertCircle className="w-4 h-4 text-[#FF3B30] mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-[14px] text-[#202020] leading-relaxed">
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* GST Calculation */}
          {(selectedPlanPrice > 0 || isComplimentary) && (
            <GSTCalculator baseAmount={selectedPlanPrice} className="mb-6" />
          )}

          {/* Loading skeleton for GST when fetching from API */}
          {shouldFetchFromAPI && loading && (
            <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            </div>
          )}

          {/* Payment Failed Banner — shows exact API error (e.g. active subscription message) */}
          {paymentStatus === "failed" && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start text-red-800">
                <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-sm font-medium">
                  {paymentErrorMessage
                    ? `Payment initiation failed: ${paymentErrorMessage}`
                    : "Payment failed. Please try again."}
                </span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={onBack}
              disabled={paymentStatus === "processing"}
              className={`flex-1 border border-gray-300 text-[#646464] py-3 px-6 rounded-lg font-medium transition-colors ${
                paymentStatus === "processing"
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-50"
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handlePayment}
              disabled={isButtonDisabled()}
              className={`flex-1 py-3 px-6 rounded-lg font-medium transition-colors ${
                paymentStatus === "failed"
                  ? "bg-[#0DB5B5] text-white hover:bg-[#0A9999]"
                  : !isButtonDisabled()
                  ? "bg-[#0DB5B5] text-white hover:bg-[#0A9999]"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              {paymentStatus === "processing" ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {getButtonText()}
                </div>
              ) : isLoadingEmail ? (
                "Loading..."
              ) : shouldFetchFromAPI && loading ? (
                "Loading Plan Details..."
              ) : selectedPlanPrice <= 0 && !loading && !isComplimentary ? (
                "Plan Details Not Available"
              ) : (
                getButtonText()
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecommendedPlanDetailsForDesktop;